import type { GetExpenseResponse, GetUserType, GetPayUpType } from '@orbit/api-types'
import { formatUnixIsoDate } from '@lib/datetime'
import { formatMoneyPlain } from '@lib/money'

/**
 * Ledger export — builds the trip's CSV ledger: Expenses and PayUps as one
 * chronological file with each involved member's signed net impact per row.
 *
 * Split math deliberately mirrors the backend settlement solver
 * (paymentOwed.service.ts): every participant share is **floored** to the
 * stored unit and the payer is credited the *sum of floored shares* — so the
 * payer absorbs rounding remainders and each row's member columns sum to
 * exactly zero, agreeing with in-app balances rather than naive division.
 */

export type LedgerExportInput = {
	expenses: GetExpenseResponse[]
	payUps: GetPayUpType[]
	members: GetUserType[]
}

const FIXED_HEADERS = ['Date', 'Type', 'Description', 'Paid by', 'Paid to', 'Split', 'Amount', 'Currency']

const SPLIT_LABELS: Record<GetExpenseResponse['splitType'], string> = {
	Equally: 'Equally',
	Value: 'By value',
	Percent: 'By percent',
}

const fullName = (member: GetUserType): string =>
	[member.firstName, member.lastName].filter(Boolean).join(' ')

/**
 * Escape a user-controlled cell (names, descriptions — arbitrary member input).
 * RFC 4180 quoting for commas/quotes/newlines, plus a leading apostrophe when
 * the value starts with a formula trigger (including after whitespace/control
 * characters) so Excel/Sheets render it as text instead of executing it.
 * Module-generated cells (dates, amounts, labels) never need this and stay bare
 * so spreadsheets parse them as values.
 */
const startsWithSpreadsheetFormula = (value: string): boolean => {
	let index = 0
	while (index < value.length && value.charCodeAt(index) <= 0x20) index += 1
	return value[index] === '=' || value[index] === '+' || value[index] === '-' || value[index] === '@'
}

const userCell = (value: string): string => {
	const neutralized = startsWithSpreadsheetFormula(value) ? `'${value}` : value
	return /[",\n\r]/.test(neutralized) ? `"${neutralized.replace(/"/g, '""')}"` : neutralized
}

/** Mirrors PaymentOwedService.getParticipantPayment — pre-floor share. */
const participantShare = (
	expense: GetExpenseResponse,
	participant: GetExpenseResponse['participants'][number],
): number => {
	if (expense.splitType === 'Equally') return expense.amount / expense.participants.length
	if (expense.splitType === 'Percent') return (participant.splitAmount ?? 0) * (expense.amount / 100)
	return participant.splitAmount ?? 0
}

/** Signed net impact per userId for one expense row; columns sum to zero. */
const expenseImpacts = (expense: GetExpenseResponse): Map<number, number> => {
	const impacts = new Map<number, number>()
	let ownerOwed = 0
	for (const participant of expense.participants) {
		const share = Math.floor(participantShare(expense, participant))
		impacts.set(participant.userId, (impacts.get(participant.userId) ?? 0) - share)
		ownerOwed += share
	}
	impacts.set(expense.paidByUserId, (impacts.get(expense.paidByUserId) ?? 0) + ownerOwed)
	return impacts
}

/** Signed net impact per userId for one payup row: sender +, receiver −. */
const payUpImpacts = (payUp: GetPayUpType): Map<number, number> =>
	new Map([
		[payUp.fromUserId, payUp.amount],
		[payUp.toUserId, -payUp.amount],
	])

/**
 * Column owners: every user who appears in any expense or payup — the same
 * participant-discovery rule the backend solver uses. Members who never
 * transacted get no column; departed members (absent from the current member
 * list) keep theirs. Known members in member-list order, then departed users
 * by ascending id.
 */
const involvedUserIds = ({ expenses, payUps, members }: LedgerExportInput): number[] => {
	const involved = new Set<number>([
		...expenses.flatMap(e => [e.paidByUserId, ...e.participants.map(p => p.userId)]),
		...payUps.flatMap(p => [p.fromUserId, p.toUserId]),
	])
	const known = members.filter(m => involved.has(m.id)).map(m => m.id)
	const knownSet = new Set(known)
	const departed = [...involved].filter(id => !knownSet.has(id)).sort((a, b) => a - b)
	return [...known, ...departed]
}

export const buildLedgerCsv = (input: LedgerExportInput): string => {
	const { expenses, payUps, members } = input
	const columnIds = involvedUserIds(input)
	const baseName = (id: number): string => {
		const member = members.find(m => m.id === id)
		// Same fallback the Balances UI uses for departed members, plus the id
		// so two departed members can't collapse into one ambiguous column.
		return member ? fullName(member) : `Unknown (#${id})`
	}
	// Two involved members can share a full name; suffix the id so headers and
	// Paid by/Paid to cells stay unambiguous (and consistent with each other).
	const nameCounts = new Map<string, number>()
	for (const id of columnIds) {
		const name = baseName(id)
		nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1)
	}
	const nameById = (id: number): string => {
		const name = baseName(id)
		return (nameCounts.get(name) ?? 0) > 1 ? `${name} (#${id})` : name
	}

	const header = [...FIXED_HEADERS, ...columnIds.map(id => userCell(nameById(id)))]
	const memberCells = (impacts: Map<number, number>, currencyCode: string): string[] =>
		columnIds.map(id => {
			const impact = impacts.get(id)
			return impact === undefined ? '' : formatMoneyPlain(impact, currencyCode)
		})

	const entries = [
		...expenses.map(expense => ({
			date: expense.expenseDate,
			id: expense.id,
			cells: [
				formatUnixIsoDate(expense.expenseDate),
				'Expense',
				userCell(expense.name),
				userCell(nameById(expense.paidByUserId)),
				'',
				SPLIT_LABELS[expense.splitType],
				formatMoneyPlain(expense.amount, expense.currencyCode),
				expense.currencyCode.toUpperCase(),
				...memberCells(expenseImpacts(expense), expense.currencyCode),
			],
		})),
		...payUps.map(payUp => ({
			date: payUp.payUpDate,
			id: payUp.id,
			cells: [
				formatUnixIsoDate(payUp.payUpDate),
				'PayUp',
				'',
				userCell(nameById(payUp.fromUserId)),
				userCell(nameById(payUp.toUserId)),
				'',
				formatMoneyPlain(payUp.amount, payUp.currencyCode),
				payUp.currencyCode.toUpperCase(),
				...memberCells(payUpImpacts(payUp), payUp.currencyCode),
			],
		})),
	]

	// Ledger convention: oldest first, like a statement. Same-day rows fall
	// back to creation order (id).
	entries.sort((a, b) => a.date - b.date || a.id - b.id)

	// BOM so Excel decodes UTF-8 (accented member names) instead of mojibake.
	return '\uFEFF' + [header, ...entries.map(e => e.cells)].map(cells => cells.join(',')).join('\r\n')
}

/** `<trip-slug>-ledger-<YYYY-MM-DD>.csv`, stamped with the viewer's export day. */
export const buildLedgerFilename = (tripName: string, exportDate: Date): string => {
	const slug =
		tripName
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '') || 'trip'
	const day = `${exportDate.getFullYear()}-${String(exportDate.getMonth() + 1).padStart(2, '0')}-${String(
		exportDate.getDate(),
	).padStart(2, '0')}`
	return `${slug}-ledger-${day}.csv`
}
